import { FunctionComponent, ReactNode, useEffect, useRef, useState } from 'react';
import { useGlobalBanner } from '@/contexts';
import classNames from 'clsx';
import { FaIcon, type FaIconName } from '@/components/FaIcon';

export type BannerVariant = 'warning' | 'error' | 'info' | 'success';

export interface GlobalBannerProps {
  variant: BannerVariant;
  message: string;
  isVisible: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
  dismissible?: boolean;
  /**
   * Todavía no se sabe si corresponde mostrarlo (falta la sesión o alguna de sus
   * queries). Mientras tanto, si en la visita anterior hubo banner, se reserva su
   * alto: cuando aparece no empuja toda la app hacia abajo.
   */
  pending?: boolean;
  /**
   * Aviso que se muestra SIEMPRE, arriba del banner por prioridad y sin competir con él
   * (hoy: WhatsApp pausado). Va dentro del mismo contenedor para que `--app-banner-h`
   * cuente los dos.
   */
  pinned?: ReactNode;
}

// Alto (px) del banner en la última visita; '0' si no hubo.
const HEIGHT_HINT_KEY = 'agora:shell:banner-h';

// Debe coincidir con `duration-200` de la salida.
const BANNER_EXIT_MS = 200;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

type BannerSnapshot = Pick<GlobalBannerProps, 'variant' | 'message' | 'action' | 'dismissible'>;

const sameBanner = (a: BannerSnapshot | null, b: BannerSnapshot) =>
  !!a &&
  a.variant === b.variant &&
  a.message === b.message &&
  a.dismissible === b.dismissible &&
  a.action?.label === b.action?.label;

const readHeightHint = (): number => {
  try {
    return Number(localStorage.getItem(HEIGHT_HINT_KEY)) || 0;
  } catch {
    return 0;
  }
};

type VariantTokens = {
  icon: FaIconName;
  kicker: string;
  /** Brand-coloured accent used for the icon, kicker, rim, and CTA border/text. */
  accent: string;
  /** Soft tint used only for the icon chip halo & leading rim glow. */
  chipBg: string;
  /** 1px inset ring on the icon chip. */
  chipRing: string;
  /** Text colour for the message body — keeps frost neutral palette. */
  textStrong: string;
};

const variants: Record<BannerVariant, VariantTokens> = {
  warning: {
    icon: 'triangle_exclamation',
    kicker: 'Aviso',
    accent: '#B45309',
    chipBg: 'rgba(var(--f-warning-rgb), 0.16)',
    chipRing: 'rgba(var(--f-warning-rgb), 0.35)',
    textStrong: 'var(--f-ink)'
  },
  error: {
    icon: 'circle_exclamation',
    kicker: 'Alerta',
    accent: '#B91C1C',
    chipBg: 'rgba(var(--f-coral-rgb), 0.14)',
    chipRing: 'rgba(var(--f-coral-rgb), 0.32)',
    textStrong: 'var(--f-ink)'
  },
  info: {
    icon: 'circle_info',
    kicker: 'Información',
    accent: '#0050B3',
    chipBg: 'rgba(var(--f-brand-rgb), 0.12)',
    chipRing: 'rgba(var(--f-brand-rgb), 0.32)',
    textStrong: 'var(--f-ink)'
  },
  success: {
    icon: 'circle_check',
    kicker: 'Listo',
    accent: '#047857',
    chipBg: 'rgba(var(--f-success-rgb), 0.14)',
    chipRing: 'rgba(var(--f-success-rgb), 0.32)',
    textStrong: 'var(--f-ink)'
  }
};

interface BannerRowProps {
  variant: BannerVariant;
  message: string;
  action?: GlobalBannerProps['action'];
  onDismiss?: () => void;
}

/** La franja en sí, sin la animación ni el alto reservado del shell. */
export const BannerRow: FunctionComponent<BannerRowProps> = ({ variant, message, action, onDismiss }) => {
  const tokens = variants[variant] || variants.warning;

  return (
    <div
      role="alert"
      aria-live="polite"
      className="relative w-full border-b border-frost-card/60 bg-[rgba(var(--f-paper-rgb),0.95)] shadow-[inset_0_1px_0_rgba(var(--f-card-rgb),0.7)] lg:bg-[rgba(var(--f-paper-rgb),0.72)] lg:backdrop-blur-[24px] lg:backdrop-saturate-[150%]"
    >
      {/* Variant-tinted rim glow along the left edge */}
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-[2px]"
        style={{
          background: tokens.accent,
          boxShadow: `0 0 14px ${tokens.chipRing}`
        }}
      />

      {/* Soft variant wash feathering in from the left — atmospheric, not a flat tint */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(420px 180% at 0% 50%, ${tokens.chipBg}, transparent 70%)`
        }}
      />

      <div className="relative mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 py-2 sm:gap-3 sm:py-3 animate-in fade-in slide-in-from-top-1 duration-300">
          {/* Message block: chip icon + kicker + message. En el celular sin el chip: el
              mensaje entra en dos líneas en vez de tres (el filo de color ya marca la variante). */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <span
              aria-hidden
              className="shrink-0 hidden sm:inline-flex items-center justify-center w-8 h-8 rounded-full"
              style={{
                background: tokens.chipBg,
                boxShadow: `0 0 0 1px ${tokens.chipRing} inset`
              }}
            >
              <FaIcon name={tokens.icon} className="text-fs-13" style={{ color: tokens.accent }} />
            </span>

            <div className="min-w-0 flex-1">
              <span
                className="hidden sm:block frost-mono text-fs-9-5 font-bold uppercase tracking-[0.18em] leading-none mb-1"
                style={{ color: tokens.accent, opacity: 0.8 }}
              >
                {tokens.kicker}
              </span>
              <p className="text-fs-12 sm:text-fs-13-5 leading-snug font-medium break-words sm:whitespace-normal text-frost-ink">
                {message}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 sm:ml-auto">
            {action && (
              <button
                type="button"
                onClick={action.onClick}
                className="inline-flex items-center gap-1 h-7 sm:h-8 px-2.5 sm:px-3.5 rounded-lg sm:rounded-xl text-fs-11 sm:text-fs-12-5 font-semibold border bg-[rgba(var(--f-card-rgb),0.72)] lg:backdrop-blur-[14px] lg:backdrop-saturate-[150%] transition-[color,background-color,transform] hover:-translate-y-px hover:bg-[rgba(var(--f-card-rgb),0.9)] active:scale-[0.98]"
                style={{ borderColor: tokens.chipRing, color: tokens.accent }}
              >
                <span>{action.label}</span>
                <FaIcon name="arrow_right" className="text-fs-10" />
              </button>
            )}
            {onDismiss && (
              <button
                type="button"
                onClick={onDismiss}
                aria-label="Cerrar"
                className={classNames(
                  'inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl border lg:backdrop-blur-[14px] lg:backdrop-saturate-[150%] transition-[background-color,transform] duration-200',
                  'hover:bg-[rgba(var(--f-card-rgb),0.88)] active:scale-95'
                )}
                style={{
                  borderColor: tokens.chipRing,
                  background: 'rgba(var(--f-card-rgb), 0.55)',
                  color: tokens.accent
                }}
              >
                <FaIcon name="xmark" className="text-fs-11" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const GlobalBanner: FunctionComponent<GlobalBannerProps> = ({
  variant,
  message,
  isVisible,
  action,
  dismissible = false,
  pending = false,
  pinned
}) => {
  const { hideBanner } = useGlobalBanner();
  const wrapRef = useRef<HTMLDivElement>(null);

  // Salida: antes, al cerrarlo (o al resolverse el aviso del shell) el banner se
  // desmontaba en el mismo render y toda la app subía de golpe. Ahora se congela el
  // último banner visible, se desvanece y sube 200 ms (solo opacidad/transform) y recién
  // después se libera su alto. El cierre es por un clic, así que ese ajuste final cae
  // dentro de la ventana de interacción y no suma CLS.
  const live: BannerSnapshot = { variant, message, action, dismissible };
  const [snapshot, setSnapshot] = useState<BannerSnapshot | null>(isVisible ? live : null);
  if (isVisible && !sameBanner(snapshot, live)) setSnapshot(live);
  const isExiting = !isVisible && snapshot !== null;
  const shown = isVisible ? live : snapshot;

  useEffect(() => {
    if (!isExiting) return undefined;
    const timer = window.setTimeout(() => setSnapshot(null), prefersReducedMotion() ? 0 : BANNER_EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [isExiting]);

  const [heightHint] = useState(readHeightHint);
  const hasContent = isVisible || !!pinned;
  const reservedHeight = pending && !hasContent ? heightHint : 0;

  // Recordar el alto para la próxima carga, solo cuando ya se sabe si va o no.
  useEffect(() => {
    if (pending && !hasContent) return;
    const height = hasContent ? Math.round(wrapRef.current?.getBoundingClientRect().height ?? 0) : 0;
    try {
      localStorage.setItem(HEIGHT_HINT_KEY, String(height));
    } catch {
      /* storage unavailable — the next load just won't reserve */
    }
  }, [pending, hasContent, isVisible, message]);

  // Publish the banner's live height as a CSS variable so the desktop Rail can
  // shrink to the remaining viewport height (`calc(100vh - var)`) instead of
  // overflowing. Previously the banner sat in normal flow above a `h-screen`
  // Rail, leaving the Rail taller than its viewport slot — its footer fell below
  // the fold at the top of the page and forced a "double scroll" to reach the
  // menu. The banner stays in normal flow (it scrolls away as before, so it never
  // occludes the many in-page `sticky top-0` headers). The wrapper is always
  // mounted (height 0 when no banner) so the observer survives banner show/hide
  // and tracks the framer-motion height animation frame-by-frame.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const root = document.documentElement;
    const sync = () =>
      root.style.setProperty('--app-banner-h', `${Math.round(el.getBoundingClientRect().height)}px`);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.setProperty('--app-banner-h', '0px');
    };
  }, []);

  return (
    <div ref={wrapRef}>
      {reservedHeight > 0 && (
        // Mismo vidrio que el banner, sin contenido: el lugar queda tomado hasta saber.
        <div
          aria-hidden
          className="w-full border-b border-frost-card/60 bg-[rgba(var(--f-paper-rgb),0.95)] lg:bg-[rgba(var(--f-paper-rgb),0.72)] lg:backdrop-blur-[24px] lg:backdrop-saturate-[150%]"
          style={{ height: reservedHeight }}
        />
      )}
      {pinned}
      {shown && (
        <div
          aria-hidden={isExiting || undefined}
          className={classNames(
            'overflow-hidden',
            isExiting
              ? 'pointer-events-none animate-out fade-out slide-out-to-top-2 fill-mode-forwards duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:animate-none'
              : 'animate-in fade-in duration-200'
          )}
        >
          <BannerRow
            variant={shown.variant}
            message={shown.message}
            action={shown.action}
            onDismiss={shown.dismissible ? hideBanner : undefined}
          />
        </div>
      )}
    </div>
  );
};
